/**
 * GenericFinancing — /project/:id/financing
 * Financial model + sensitivity + carbon pathway — same structure as HousingFinancing.
 * Reads from normalized sector output — no sector-specific logic.
 * Capital activation: "Initiate Financing Process" button + evidence readiness + hard filters.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, Shield, CheckCircle2, XCircle, ArrowRight,
         TrendingUp, TrendingDown, AlertTriangle, Download, Zap, AlertOctagon,
         Share2, MessageCircle, FileText, ChevronRight, Send, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Sensitivity table (reusable, same as HousingFinancing) ─────────────────
function SensitivityTable({ sensitivity }) {
  if (!sensitivity) return null;
  const base = sensitivity.base || {};
  const scenarios = sensitivity.scenarios || [];
  const payback = sensitivity.payback_analysis || {};
  const capexS   = scenarios.filter(s => s.variable === "capex");
  const genS     = scenarios.filter(s => s.variable === "generation" || s.variable === "feedstock" || s.variable === "biogas_yield" || s.variable === "energy");
  const carbonS  = scenarios.filter(s => s.variable === "carbon_price");

  function DeltaBadge({ pct }) {
    if (pct == null) return null;
    const pos = pct > 0;
    return <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${pos ? "text-red-600" : "text-emerald-600"}`}>
      {pos ? <TrendingUp size={9}/> : <TrendingDown size={9}/>}{pos?"+":""}{pct}%
    </span>;
  }

  function ScenarioTable({ rows, columns, label }) {
    if (!rows.length) return null;
    return (
      <div className="mb-4">
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">{label}</p>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              {columns.map(c => <th key={c} className="text-left text-xs text-slate-400 pb-1.5 pr-3 font-semibold">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b border-slate-50">
                <td className="py-1.5 pr-3 text-slate-600">{row.label}</td>
                {row.cells.map((c, i) => (
                  <td key={i} className="py-1.5 pr-3 font-bold text-slate-800">{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const baseRed = base.reduction_tco2e_yr || 0;
  const baseSav = base.annual_savings_usd_yr || 0;
  const baseCT  = base.cost_per_tco2e || 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="sensitivity-table">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-bold text-slate-900">Financial Sensitivity Analysis</p>
        <p className="text-xs text-slate-500 mt-0.5">{sensitivity.methodology_note}</p>
      </div>
      <div className="px-5 py-4 space-y-4">
        {/* Base */}
        <div>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">Base Case</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Reduction",   `${baseRed?.toFixed ? baseRed.toFixed(1) : baseRed} tCO₂e/yr`],
              ["Cost/tCO₂e",  `$${baseCT}`],
              ["Ann. Savings",`$${(baseSav||0).toLocaleString()}/yr`],
            ].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-lg p-2.5 text-center">
                <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                <p className="text-xs font-bold text-slate-900">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CAPEX sensitivity */}
        {capexS.length > 0 && <ScenarioTable label="CAPEX Sensitivity" columns={["Scenario","CAPEX","Cost/tCO₂e"]}
          rows={capexS.map(s => ({id:s.id, label:s.label, cells:[`$${(s.capex_usd||0).toLocaleString()}`,`$${s.cost_per_tco2e}`]}))} />}

        {/* Generation/feedstock sensitivity */}
        {genS.length > 0 && <ScenarioTable label="Output Sensitivity" columns={["Scenario","Reduction","Savings"]}
          rows={genS.map(s => ({id:s.id, label:s.label, cells:[
            `${(s.reduction_tco2e_yr||0).toFixed?s.reduction_tco2e_yr.toFixed(2):s.reduction_tco2e_yr} tCO₂e/yr`,
            `$${(s.annual_savings_usd_yr||s.energy_savings_usd_yr||0).toLocaleString()}/yr`,
          ]}))} />}

        {/* Carbon price sensitivity */}
        {carbonS.length > 0 && <ScenarioTable label="Carbon Price Sensitivity" columns={["Scenario","Price Range","3yr Rev Low","3yr Rev High"]}
          rows={carbonS.map(s => ({id:s.id, label:s.label, cells:[
            s.price_range_usd_t || s.price_range || "—",
            `$${(s.carbon_rev_low_3yr||s.carbon_rev_low_3yr_prog||0).toLocaleString()}`,
            `$${(s.carbon_rev_high_3yr||s.carbon_rev_high_3yr_prog||0).toLocaleString()}`,
          ]}))} />}

        {payback.simple_payback_years_per_unit && (
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-400 font-semibold uppercase">Simple Payback</p>
              <p className="text-xs text-slate-400 italic">{payback.note}</p>
            </div>
            <p className="text-lg font-bold text-slate-900">{payback.simple_payback_years_per_unit} <span className="text-xs font-normal text-slate-400">yr</span></p>
          </div>
        )}
      </div>
      <div className="px-5 pb-3">
        <p className="text-xs text-amber-600 italic">{sensitivity.disclaimer}</p>
      </div>
    </div>
  );
}

export default function GenericFinancing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Role-based protection: FI deal workspace is an FI/Admin-only feature
  // Project owners (role="user") must NOT see FI deal objects — prevents cross-project leakage
  const isFiUser = user?.role === "fi" || user?.role === "admin";

  const [dlBrief,    setDlBrief]    = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [dealRecord,  setDealRecord]  = useState(null);
  const [dealDetail,  setDealDetail]  = useState(null);
  const [evScore,     setEvScore]     = useState(null);
  const [advancing,   setAdvancing]   = useState(false);
  // Deal room
  const [messages,     setMessages]     = useState([]);
  const [docRequests,  setDocRequests]  = useState([]);
  const [timeline,     setTimeline]     = useState([]);
  const [newMsg,       setNewMsg]       = useState("");
  const [sendingMsg,   setSendingMsg]   = useState(false);
  const [showConvo,    setShowConvo]    = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showShare,    setShowShare]    = useState(false);
  const [shareEmail,   setShareEmail]   = useState("");
  const [shareNote,    setShareNote]    = useState("");
  const [sharing,      setSharing]      = useState(false);
  const [shareResult,  setShareResult]  = useState(null);

  const {
    loading, isEmpty, financial, sensitivity, carbonPathway, pathways,
    reduction, kpis, sectorLabel, org, calcVersion, workflowStatus, sector,
    journey, projectState,
  } = useProjectWorkspace(id);

  const dscrData    = journey?.dscr            || {};
  const projReturns = journey?.project_returns || {};
  const capexSens   = journey?.capex_sensitivity || {};
  const dealPathways= dscrData.deal_pathways   || {};
  const investorStatus = journey?.investor_status;
  const dealReadiness  = journey?.deal_readiness  || null;
  const validationStatus = journey?.validation_status || null;
  const dataConfidence   = journey?.data_confidence   || null;
  const assumptionFlags  = journey?.assumption_flags_rated || [];

  // Fetch evidence readiness score
  useEffect(() => {
    if (!id) return;
    client.get(`/workspace/project/${id}/evidence`)
      .then(r => {
        const cov = r.data?.coverage || {};
        setEvScore(cov.verified_pct || cov.coverage_pct || 0);
      }).catch(() => {});
  }, [id]);

  // Check if a deal already exists for this project
  // PROJECT ISOLATION RULE: Only FI/Admin users fetch deal objects.
  // Project owners never see deal workspace — prevents cross-project leakage.
  useEffect(() => {
    if (!id || !isFiUser) return;
    client.get("/fi/deals")
      .then(r => {
        const allDeals = r.data?.deals || [];
        // Hard scope guard: ONLY use deals tied to THIS project's assessment_id
        // Do NOT fall back to any deal with a different assessment_id
        const existing = allDeals.find(
          d => String(d.assessment_id) === String(id) &&
               !["financial_close","rejected"].includes(d.status)
        );
        if (existing) {
          setDealRecord(existing);
          client.get(`/fi/deals/${existing.deal_id}`)
            .then(r2 => setDealDetail(r2.data))
            .catch(() => {});
        }
        // Explicitly do NOT use terminal (financial_close/rejected) deals
        // and do NOT render any deal whose assessment_id ≠ current project
      }).catch(() => {});
  }, [id, isFiUser]);

  const handleInitiateDeal = async () => {
    setInitiating(true);
    try {
      const res = await client.post("/fi/initiate-deal", { assessment_id: parseInt(id) });
      setDealRecord(res.data);
      const detail = await client.get(`/fi/deals/${res.data.deal_id}`);
      setDealDetail(detail.data);
      toast.success(`Deal initiated: ${res.data.deal_id} — now in Screening stage.`);
    } catch (e) {
      toast.error("Failed to initiate deal. Please check your permissions.");
    }
    setInitiating(false);
  };

  const handleAdvanceDeal = async (action, notes = null) => {
    if (!dealRecord?.deal_id) return;
    setAdvancing(true);
    try {
      const res = await client.post("/fi/advance-deal-stage", {
        deal_id: dealRecord.deal_id, action, notes,
      });
      // Refresh deal detail
      const detail = await client.get(`/fi/deals/${dealRecord.deal_id}`);
      setDealDetail(detail.data);
      setDealRecord(prev => ({ ...prev, status: res.data.current_stage }));
      toast.success(`Deal advanced: ${res.data.previous_stage} → ${res.data.current_stage}`);
    } catch (e) {
      const err = e.response?.data;
      if (err?.blockers) {
        toast.error(`Gate check failed: ${err.blockers.join(" | ")}`);
      } else {
        toast.error(err?.message || "Transition failed.");
      }
    }
    setAdvancing(false);
  };

  // Load deal room data when deal is active (FI users only)
  useEffect(() => {
    if (!dealRecord?.deal_id || !isFiUser) return;
    const did = dealRecord.deal_id;
    Promise.all([
      client.get(`/deal/${did}/messages`).then(r => setMessages(r.data?.messages || [])),
      client.get(`/deal/${did}/document-requests`).then(r => setDocRequests(r.data?.requests || [])),
    ]).catch(() => {});
  }, [dealRecord?.deal_id]);

  const loadTimeline = async () => {
    if (!dealRecord?.deal_id) return;
    try {
      const r = await client.get(`/deal/${dealRecord.deal_id}/timeline`);
      setTimeline(r.data?.events || []);
    } catch {}
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !dealRecord?.deal_id) return;
    setSendingMsg(true);
    try {
      const r = await client.post(`/deal/${dealRecord.deal_id}/messages`, { message: newMsg });
      setMessages(prev => [...prev, r.data]);
      setNewMsg("");
    } catch { toast.error("Failed to send message."); }
    setSendingMsg(false);
  };

  const shareDeal = async () => {
    if (!shareEmail || !dealRecord?.deal_id) return;
    setSharing(true);
    try {
      const r = await client.post("/deal/share", {
        deal_id: dealRecord.deal_id, investor_email: shareEmail, message: shareNote || null,
      });
      setShareResult(r.data);
      toast.success(`Deal shared with ${shareEmail}${r.data.email_sent ? " — invite sent" : " — copy link below"}`);
    } catch { toast.error("Failed to share deal."); }
    setSharing(false);
  };

  const downloadFinanceBrief = async () => {
    const briefEndpoints = { biogas: "biogas-finance-brief", waste_recovery: "biogas-finance-brief" };
    const endpoint = briefEndpoints[sector];
    if (!endpoint) { toast.error("Finance brief not yet available for this sector."); return; }
    setDlBrief(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${id}/${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `FinanceBrief-${id}.pdf`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Finance Brief downloaded.");
    } catch (e) { toast.error("Download failed — please retry."); }
    setDlBrief(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const isFinanceReady = workflowStatus === "verified";
  const hasFinanceInputs = financial?.capex_usd != null && Number(financial.capex_usd) > 0;

  // CDE: use project_state for financing signal (not locally computed)
  const cdeFinancial   = projectState?.financial || {};
  const cdeSignal      = cdeFinancial.financing_signal || "INSUFFICIENT_DATA";
  const cdeReason      = cdeFinancial.financing_reason || "";
  const cdeWarnings    = projectState?.overall?.warnings || [];

  // Signal display config (CDE-driven — no local computation)
  const SIGNAL_CFG = {
    INVESTABLE:          { label: "Financial Model: Structurally Viable",   border: "border-emerald-300 bg-emerald-50", icon: "bg-emerald-100", iconColor: "text-emerald-600", sub: "DSCR ≥ 1.2x — structured for DFI pre-screening. Subject to independent technical and financial due diligence." },
    RESTRUCTURE_REQUIRED:{ label: "Restructuring Required",                 border: "border-amber-300 bg-amber-50",     icon: "bg-amber-100",   iconColor: "text-amber-600",   sub: "DSCR below 1.2x DFI minimum — restructuring levers available" },
    NOT_FINANCEABLE:     { label: "Not Financeable — Restructuring Required",border: "border-red-400 bg-red-50",         icon: "bg-red-100",     iconColor: "text-red-600",     sub: "DSCR < 1.0x — not financeable under any standard DFI structure" },
    NOT_INVESTMENT_READY:{ label: "Validation Readiness Threshold Not Met",  border: "border-red-300 bg-red-50",         icon: "bg-red-100",     iconColor: "text-red-600",     sub: "Evidence coverage below threshold (30%) — address evidence gaps before financing assessment" },
    INSUFFICIENT_DATA:   { label: "Insufficient Data",                       border: "border-slate-200 bg-white",        icon: "bg-slate-100",   iconColor: "text-slate-500",   sub: "Enter CAPEX and revenue inputs to compute financial model" },
  };
  const signalCfg = SIGNAL_CFG[cdeSignal] || SIGNAL_CFG.INSUFFICIENT_DATA;

  return (
    <div className="p-6 lg:p-8 min-h-screen">

        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> {sectorLabel}
        </button>

        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
          <PageHeader title="Financing" />
          <p className="text-xs text-slate-400 mt-0.5">
            Financial Model — Validation-Ready Outputs · {calcVersion?.label || "awaiting calculation"}
          </p>
        </div>

        {isEmpty && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
            <AlertTriangle size={24} className="text-amber-400 mx-auto mb-3" />
            <p className="text-sm font-bold text-amber-800">No financial data yet</p>
            <p className="text-xs text-amber-600 mb-4">Enter project inputs to generate the financial model.</p>
            <button onClick={() => navigate(`/project/${id}/input-data`)}
              className="px-5 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700">
              Enter Inputs
            </button>
          </div>
        )}

        {!isEmpty && financial && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Left 2/3 */}
            <div className="lg:col-span-2 space-y-5">

              {/* CDE Critical Warnings — shown above all other content */}
              {cdeWarnings.filter(w => w.level === "critical").map((w, i) => (
                <div key={i} className="border-2 border-red-500 bg-red-50 rounded-xl p-4 flex items-start gap-3" data-testid="cde-critical-warning">
                  <AlertOctagon size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide">{w.code?.replace(/_/g," ")}</p>
                    <p className="text-xs text-red-600 mt-0.5">{w.message}</p>
                  </div>
                </div>
              ))}

              {/* DO NOT PRESENT hard banner — only when finance inputs exist */}
              {hasFinanceInputs && investorStatus === "do_not_present" && cdeSignal !== "NOT_FINANCEABLE" && (
                <div className="border-2 border-red-500 bg-red-50 rounded-xl p-5 flex items-start gap-3" data-testid="do-not-present-banner">
                  <AlertOctagon size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-700 uppercase tracking-wide">DO NOT PRESENT TO INVESTORS</p>
                    <p className="text-xs text-red-600 mt-1">
                      DSCR is below 1.0x with no viable restructuring pathway. This project is not financeable
                      under any standard DFI structure. Complete the restructuring requirements before any
                      lender or investor engagement.
                    </p>
                  </div>
                </div>
              )}

              {/* Validation Status Banner (Part 1) */}
              {validationStatus && (
                <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-2.5 flex items-center gap-2.5" data-testid="validation-status-banner">
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-wide">
                    {validationStatus.platform_only_note}
                  </span>
                  <span className="text-xs text-amber-500 italic flex-1">
                    · {validationStatus.vvb_required ? "VVB engagement required before carbon credit issuance" : ""}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    validationStatus.validation_status === "validation_ready" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"
                  }`}>{validationStatus.validation_label?.split("—")[0]?.trim()}</span>
                </div>
              )}

              {/* Data Confidence Score (Part 4) */}
              {dataConfidence && (
                <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="data-confidence">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Data Confidence</p>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          dataConfidence.label === "High" ? "bg-emerald-500" : dataConfidence.label === "Medium" ? "bg-amber-500" : "bg-red-500"
                        }`} style={{ width: `${dataConfidence.data_confidence_score}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${
                        dataConfidence.label === "High" ? "text-emerald-700" : dataConfidence.label === "Medium" ? "text-amber-700" : "text-red-600"
                      }`}>{dataConfidence.data_confidence_score}/100 ({dataConfidence.label})</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    {[
                      ["Primary Data",  dataConfidence.confidence_breakdown?.primary_data,    "60"],
                      ["Assumptions",   dataConfidence.confidence_breakdown?.assumptions,     "25"],
                      ["Factors",       dataConfidence.confidence_breakdown?.default_factors, "15"],
                    ].map(([l, v, max]) => (
                      <div key={l} className="bg-slate-50 rounded-lg py-1.5">
                        <p className="text-xs text-slate-400">{l}</p>
                        <p className="text-xs font-bold text-slate-700">{v}<span className="text-slate-400 font-normal">/{max}</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Critical Assumptions Panel (Part 3) */}
              {assumptionFlags.length > 0 && assumptionFlags.some(f => f.risk_level === "high") && (
                <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-testid="assumptions-panel">
                  <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 flex items-center gap-2">
                    <span className="text-xs font-bold text-red-600 uppercase tracking-wide">Critical Assumptions — High Risk</span>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    {assumptionFlags.map((f, i) => {
                      const risk = f.risk_level || "low";
                      const colors = { high:"text-red-600 bg-red-50", medium:"text-amber-700 bg-amber-50", low:"text-slate-500 bg-slate-50" };
                      return (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${colors[risk]}`}>{risk.toUpperCase()}</span>
                          <span className="text-slate-600">{f.text?.slice(0, 100)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Evidence readiness — CDE driven (threshold 30%, not 70%) */}
              {cdeSignal === "NOT_INVESTMENT_READY" && (
                <div className="border border-red-300 bg-red-50 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="evidence-gap-banner">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-red-800">VALIDATION READINESS THRESHOLD NOT MET — EVIDENCE GAPS</p>
                      <p className="text-xs text-red-600">{cdeReason}</p>
                    </div>
                  </div>
                  <button onClick={() => navigate(`/project/${id}/evidence-vault`)}
                    className="flex-shrink-0 text-xs font-bold text-red-700 border border-red-300 px-3 py-1.5 rounded-lg hover:bg-red-100">
                    Upload Evidence
                  </button>
                </div>
              )}

              {/* Deal Readiness Panel */}
              {dealReadiness && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="deal-readiness-panel">
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-900">Deal Readiness</p>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-20 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${dealReadiness.overall_score >= 70 ? "bg-emerald-500" : dealReadiness.overall_score >= 40 ? "bg-amber-500" : "bg-red-500"}`}
                          style={{ width: `${dealReadiness.overall_score}%` }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700">{dealReadiness.overall_score}/100</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    {/* Category breakdown */}
                    {Object.entries(dealReadiness.evidence_by_category || {}).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 capitalize">{cat}</span>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${(data.pct||0) >= 100 ? "bg-emerald-500" : (data.pct||0) > 50 ? "bg-amber-500" : "bg-red-400"}`}
                              style={{ width: `${data.pct || 0}%` }} />
                          </div>
                          <span className="text-xs font-bold text-slate-600">{data.verified}/{data.required}</span>
                          {data.critical_missing > 0 && (
                            <span className="text-xs text-red-500 font-bold">⚠ {data.critical_missing} critical</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* Required actions */}
                    {dealReadiness.required_actions?.length > 0 && (
                      <div className="border-t border-slate-100 pt-3">
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wide mb-2">
                          Required Actions ({dealReadiness.credit_committee_ready ? "0 blocking" : dealReadiness.critical_blockers?.length + " blocking"})
                        </p>
                        {dealReadiness.required_actions.slice(0, 4).map((a, i) => (
                          <div key={i} className="flex items-start gap-1.5 text-xs py-0.5">
                            <span className={`font-bold flex-shrink-0 ${i === 0 && !dealReadiness.credit_committee_ready ? "text-red-500" : "text-amber-500"}`}>{i+1}.</span>
                            <span className="text-slate-600">{a}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Financing Signal — CDE driven, not locally computed */}
              <div className={`rounded-xl p-6 border-2 ${signalCfg.border}`} data-testid="financing-signal">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Financing Signal</p>
                    <p className="text-2xl font-bold text-slate-900">{signalCfg.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{signalCfg.sub}</p>
                  </div>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${signalCfg.icon}`}>
                    <Shield size={18} className={signalCfg.iconColor} />
                  </div>
                </div>
                {/* CDE reason — always show */}
                <div className="mt-3 pt-3 border-t border-slate-100/80">
                  <p className="text-xs text-slate-500 italic">{cdeReason}</p>
                  <p className="text-xs text-amber-600 italic mt-1">
                    Based on modeled assumptions — subject to independent technical and financial due diligence. Not a final investment assessment.
                  </p>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Model Stage</p>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                    isFinanceReady ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-700"
                  }`}>
                    {isFinanceReady ? "Validation-Ready Model" : "Pre-Verification Model"}
                  </span>
                </div>
              </div>

              {/* A. Core Financials — Bankable */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-teal-50 border-b border-teal-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-teal-600 font-bold uppercase tracking-wide">A. Core Financials — Modeled</p>
                      <p className="text-sm font-bold text-teal-800">Project Economics (Bankable Streams Only)</p>
                      <p className="text-xs text-teal-600">{financial.annual_savings_type?.replace(/_/g," ") || "Energy revenue + tipping fees"}</p>
                    </div>
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full border border-teal-200 font-semibold">Carbon revenue excluded</span>
                  </div>
                </div>
                <div className="px-5 py-4 space-y-2.5">
                  {[
                    ["Total CAPEX",        financial.capex_usd ? `$${Number(financial.capex_usd).toLocaleString()}` : "—",         "text-slate-900"],
                    ["Annual OPEX",        financial.opex_usd_yr ? `$${Number(financial.opex_usd_yr).toLocaleString()}/yr` : "—",   "text-slate-600"],
                    ["Annual Net CF",      financial.annual_savings_usd_yr ? `$${Number(financial.annual_savings_usd_yr).toLocaleString()}/yr` : "—", "text-emerald-700"],
                    ["Project IRR",        hasFinanceInputs && projReturns.project_irr_pct != null ? `${projReturns.project_irr_pct}%` : "—",           "text-teal-700"],
                    ["Payback Period",     hasFinanceInputs && financial.simple_payback_years ? `${financial.simple_payback_years} yr` : "—",            "text-teal-700"],
                    ["Cost per tCO₂e",     hasFinanceInputs && financial.cost_per_tco2e ? `$${financial.cost_per_tco2e}` : "—",                         "text-teal-700"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="flex items-start justify-between">
                      <p className="text-xs font-semibold text-slate-700">{l}</p>
                      <span className={`text-sm font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                  {!hasFinanceInputs && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-amber-600 italic">
                        CAPEX not entered — IRR, DSCR, and payback require project cost inputs.
                        Enter CAPEX in the Input Data tab to unlock financial analysis.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* DSCR — Debt Service Coverage — only when capex is provided */}
              {hasFinanceInputs && dscrData.base && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="dscr-section">
                  {/* Hard flag when DSCR < 1.0 */}
                  {dscrData.base.dscr_flag === "not_financeable" && (
                    <div className="px-5 py-3 bg-red-600 flex items-center gap-2">
                      <span className="text-white font-bold text-sm">!</span>
                      <p className="text-xs font-bold text-white uppercase tracking-wide">
                        NOT FINANCEABLE UNDER CURRENT STRUCTURE — Restructuring required before lender engagement
                      </p>
                    </div>
                  )}
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-900">Debt Service Coverage (DSCR)</p>
                      <p className="text-xs text-slate-500">EBITDA ÷ Annual Debt Service · DFI minimum: 1.2–1.25x</p>
                      <p className="text-xs text-amber-600 italic mt-0.5">Based on modeled assumptions — subject to independent technical and financial due diligence</p>
                    </div>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    {[
                      { label: "DSCR — Base case",  data: dscrData.base,  note: "Full feedstock volume" },
                      { label: "DSCR — P90 stress", data: dscrData.p90,   note: `P90 feedstock (-${journey?.feedstock_supply_security?.volume_variability_pct || 20}% variability)` },
                    ].map(({ label, data, note }) => {
                      if (!data) return null;
                      const flag  = data.dscr_flag;
                      const styles = { strong:"bg-emerald-100 text-emerald-800 border-emerald-200", acceptable:"bg-amber-100 text-amber-700 border-amber-200", weak:"bg-red-50 text-red-700 border-red-200", insufficient_data:"bg-slate-100 text-slate-500 border-slate-200" };
                      const labelBadge = { strong:"Structurally Viable", acceptable:"Marginal — Review", weak:"Below Threshold", insufficient_data:"N/A" };
                      return (
                        <div key={label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <div>
                            <p className="text-xs font-semibold text-slate-700">{label}</p>
                            <p className="text-xs text-slate-400">{note}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-lg font-bold text-slate-900">{data.dscr ? `${data.dscr}x` : "—"}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${styles[flag] || styles.insufficient_data}`} data-testid={`dscr-flag-${label.includes('P90') ? 'p90' : 'base'}`}>
                              {labelBadge[flag] || "—"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {dscrData.base && (
                      <p className="text-xs text-slate-400 italic">{dscrData.base.dscr_interpretation}</p>
                    )}
                    {/* Restructuring recommendations — shown only when DSCR < 1.2 */}
                    {dscrData.restructuring?.status === "restructure_required" && (
                    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="restructuring-recs">
                      <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-2">
                        To reach DSCR &ge;1.2x — one or more required:
                      </p>
                      {dscrData.restructuring.recommendations.map((rec, i) => {
                        const feasColors = { high:"text-emerald-700", medium:"text-amber-700", low:"text-red-600", not_viable:"text-slate-400" };
                        return (
                          <div key={rec.lever} className="flex items-start gap-2 py-1.5 border-b border-slate-50 last:border-0">
                            <span className="text-xs text-slate-400 font-bold w-4 flex-shrink-0">{i+1}.</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-semibold text-slate-700 truncate">{rec.label}</p>
                                <span className={`text-xs font-bold flex-shrink-0 ${feasColors[rec.feasibility] || "text-slate-500"}`}>
                                  {rec.feasibility?.replace(/_/g," ")}
                                </span>
                              </div>
                              <p className="text-xs text-teal-700 font-semibold">
                                {rec.current} &rarr; {rec.required}
                                <span className="text-slate-400 font-normal ml-1">({rec.delta})</span>
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      {dscrData.restructuring.combination_note && (
                        <p className="text-xs text-amber-600 italic mt-2">
                          {dscrData.restructuring.combination_note}
                        </p>
                      )}
                    </div>
                    )}
                    {dscrData.assumptions && (
                      <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500">
                        Debt structure: {dscrData.assumptions.debt_ratio_pct}% LTV · {dscrData.assumptions.interest_rate_pct}% interest · {dscrData.assumptions.loan_tenor_years}yr tenor
                        · Annual DS: ${Number(dscrData.base?.annual_debt_service_usd || 0).toLocaleString()}/yr
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CAPEX Sensitivity — IRR + DSCR — only when capex is provided */}
              {hasFinanceInputs && capexSens.base && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="capex-sensitivity-irr-dscr">
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                    <p className="text-sm font-bold text-slate-900">CAPEX Sensitivity — IRR & DSCR Impact</p>
                    <p className="text-xs text-slate-500">{capexSens.assumption_note}</p>
                  </div>
                  <div className="px-5 py-4 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-100">
                          {["Scenario", "CAPEX", "IRR", "DSCR", "Rating"].map(h => (
                            <th key={h} className="text-left text-xs text-slate-400 pb-1.5 pr-3 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {["minus_15", "base", "plus_15"].map(key => {
                          const s = capexSens[key] || {};
                          const isBase = key === "base";
                          const dscrStyles = { strong:"text-emerald-700", acceptable:"text-amber-700", weak:"text-red-600" };
                          return (
                            <tr key={key} className={`border-b border-slate-50 ${isBase ? "bg-slate-50" : ""}`}>
                              <td className={`py-2 pr-3 ${isBase ? "font-bold text-slate-800" : "text-slate-600"}`}>{s.label}</td>
                              <td className="py-2 pr-3 text-slate-700">${Number(s.capex_usd || 0).toLocaleString()}</td>
                              <td className={`py-2 pr-3 font-bold ${isBase ? "text-teal-700" : "text-slate-700"}`}>{s.irr_pct != null ? `${s.irr_pct}%` : "—"}</td>
                              <td className={`py-2 pr-3 font-bold ${dscrStyles[s.dscr_flag] || "text-slate-700"}`}>{s.dscr != null ? `${s.dscr}x` : "—"}</td>
                              <td className="py-2">
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                  s.dscr_flag === "strong" ? "bg-emerald-100 text-emerald-700" :
                                  s.dscr_flag === "acceptable" ? "bg-amber-100 text-amber-700" :
                                  "bg-red-50 text-red-600"
                                }`}>{(s.dscr_flag || "—").toUpperCase()}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Deal Structuring Pathways — only when capex is provided */}
              {hasFinanceInputs && dealPathways.pathways?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="deal-pathways">
                  <div className="px-5 py-3 bg-[#0F172A] flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-white">Indicative Financing Structures</p>
                      <p className="text-xs text-slate-400 mt-0.5">Scenario-based deal structures · Subject to independent due diligence</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                      dealPathways.recommended_pathway === "commercial_only" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                      dealPathways.recommended_pathway === "blended_finance"  ? "bg-teal-100 text-teal-800 border-teal-200" :
                      "bg-amber-100 text-amber-700 border-amber-200"
                    }`}>
                      {(dealPathways.recommended_pathway?.replace(/_/g," ") || "").replace(/\b\w/g,c=>c.toUpperCase())}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {dealPathways.pathways.map(p => {
                      const STATUS_STYLES = {
                        viable:        "bg-emerald-50 border-emerald-200",
                        recommended:   "bg-teal-50 border-teal-200",
                        conditional:   "bg-amber-50 border-amber-200",
                        not_viable:    "bg-slate-50 border-slate-200",
                        fallback:      "bg-slate-50 border-slate-200",
                      };
                      const STATUS_DOT = {
                        viable:"bg-emerald-500", recommended:"bg-teal-500",
                        conditional:"bg-amber-500", not_viable:"bg-slate-300", fallback:"bg-slate-300",
                      };
                      // Rename status labels — no implied final recommendation
                      const STATUS_DISPLAY = {
                        viable:      "Scenario-Based Option",
                        recommended: "Indicative Structure",
                        conditional: "Conditional Structure",
                        not_viable:  "Not Viable",
                        fallback:    "Conditional Structure",
                      };
                      const isRec = p.type === dealPathways.recommended_pathway;
                      return (
                        <div key={p.type} className={`px-5 py-4 ${isRec ? "bg-teal-50" : ""}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[p.status] || "bg-slate-300"}`} />
                              <p className="text-xs font-bold text-slate-900">{p.label}</p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {isRec && <span className="text-xs font-bold bg-teal-600 text-white px-1.5 py-0.5 rounded">INDICATIVE STRUCTURE</span>}
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.not_viable}`}>
                                {STATUS_DISPLAY[p.status] || p.status?.replace(/_/g," ")}
                              </span>
                            </div>
                          </div>
                          {/* Structure details */}
                          {p.structure && p.status !== "not_viable" && (
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              {[
                                p.structure.tipping_fee_usd_t && ["Tipping fee", `$${p.structure.tipping_fee_usd_t}/t${p.structure.tipping_fee_increase > 0 ? ` (+$${p.structure.tipping_fee_increase})`:""}`],
                                p.structure.interest_rate_pct && ["Rate", `${p.structure.interest_rate_pct}%`],
                                p.structure.loan_tenor_years  && ["Tenor", `${p.structure.loan_tenor_years}yr`],
                                p.structure.grace_period_years > 0 && ["Grace", `${p.structure.grace_period_years}yr`],
                                p.structure.grant_component_pct > 0 && ["Grant", `${p.structure.grant_component_pct}% ($${Number(p.structure.grant_amount_usd||0).toLocaleString()})`],
                                p.structure.grant_required_pct  > 0 && ["Grant req.", `${p.structure.grant_required_pct}% ($${Number(p.structure.grant_amount_usd||0).toLocaleString()})`],
                              ].filter(Boolean).slice(0,4).map(([l, v]) => (
                                <div key={l} className="bg-white/60 rounded-lg px-2 py-1.5 text-center">
                                  <p className="text-xs text-slate-400">{l}</p>
                                  <p className="text-xs font-bold text-slate-900">{v}</p>
                                </div>
                              ))}
                              {p.expected_dscr && (
                                <div className={`rounded-lg px-2 py-1.5 text-center ${
                                  p.expected_dscr_flag === "acceptable" || p.expected_dscr_flag === "strong" ? "bg-emerald-100" : "bg-red-50"
                                }`}>
                                  <p className="text-xs text-slate-500">DSCR</p>
                                  <p className="text-xs font-bold text-slate-900">{p.expected_dscr}x</p>
                                </div>
                              )}
                            </div>
                          )}
                          {p.status === "not_viable" && p.reason && (
                            <p className="text-xs text-slate-500 italic">{p.reason}</p>
                          )}
                          {p.narrative && p.status !== "not_viable" && (
                            <p className="text-xs text-teal-700 leading-snug">{p.narrative}</p>
                          )}
                        </div>
                      );
                    })}
                    <div className="px-5 py-2">
                      <p className="text-xs text-slate-400 italic">{dealPathways.deal_note}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* NOT FINANCEABLE hard flag — only when finance inputs exist */}
              {hasFinanceInputs && dscrData.base?.dscr_flag === "not_financeable" && !dealPathways.pathways?.some(p => p.status === "recommended" || p.status === "viable") && (
                <div className="border-2 border-red-400 bg-red-50 rounded-xl p-4 flex items-start gap-3" data-testid="not-financeable-banner">
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide">NOT FINANCEABLE UNDER CURRENT STRUCTURE</p>
                    <p className="text-xs text-red-600 mt-0.5">Restructuring required before any lender engagement. See recommendations above.</p>
                  </div>
                </div>
              )}
              {(financial.carbon_revenue_low_3yr || financial.carbon_rev_low_3yr) && (
                <div className="bg-white border-2 border-blue-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-blue-50 border-b border-blue-100">
                    <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">B. Conditional Upside — Carbon Revenue (Non-Bankable)</p>
                    <p className="text-sm font-bold text-blue-800">Carbon Revenue Scenarios (Indicative)</p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Indicative only — subject to successful validation, registration, and issuance.
                      Not to be included in base financing decisions or DSCR calculations.
                    </p>
                  </div>
                  <div className="px-5 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-400 mb-0.5">Low ($15/t)</p>
                      <p className="text-sm font-bold text-blue-700">${Number(financial.carbon_revenue_low_3yr || financial.carbon_rev_low_3yr || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-400 mb-0.5">High ($30/t)</p>
                      <p className="text-sm font-bold text-blue-700">${Number(financial.carbon_revenue_high_3yr || financial.carbon_rev_high_3yr || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 italic mt-2">{financial.carbon_revenue_disclaimer}</p>
                  <p className="text-xs text-red-500 italic mt-1 font-semibold">
                    Carbon revenue must NOT be included in base DSCR calculations until formal validation and credit issuance are confirmed.
                  </p>
                  </div>
                </div>
              )}

              {/* Sensitivity */}
              {sensitivity && <SensitivityTable sensitivity={sensitivity} />}

            </div>

            {/* Right 1/3 */}
            <div className="space-y-4">
              {/* Reduction summary */}
              <div className="bg-[#0F172A] rounded-xl p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Climate Impact</p>
                {(() => {
                  const annualRed = reduction?.tco2e_yr ?? reduction?.total_tco2e_yr;
                  const hasRed = annualRed != null && Number(annualRed) > 0;
                  return [
                    ["Annual Reduction", hasRed ? `${Number(annualRed).toFixed(1)} tCO₂e/yr` : "—", "text-teal-400"],
                    ["Lifetime Reduction", hasRed && reduction?.lifetime_tco2e > 0 ? `${Number(reduction.lifetime_tco2e).toLocaleString()} tCO₂e` : "—", "text-white"],
                    ["Reduction %", hasRed && reduction?.reduction_pct ? `${Number(reduction.reduction_pct).toFixed(1)}%` : "—", "text-emerald-400"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="bg-white/5 rounded-xl px-3 py-2.5 mb-2 last:mb-0">
                      <p className="text-xs text-slate-500">{l}</p>
                      <p className={`text-sm font-bold ${c}`}>{v}</p>
                    </div>
                  ));
                })()}
                {!((reduction?.tco2e_yr ?? reduction?.total_tco2e_yr) > 0) && (
                  <p className="text-xs text-slate-500 italic mt-2">
                    Enter feedstock inputs and recalculate to see climate impact.
                  </p>
                )}
              </div>

              {/* Carbon pathway summary — CDE driven */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Carbon Pathways</p>
                {[["Insetting","insetting"],["VCM","vcm"],["PoA","poa"],["Article 6","art6"]].map(([name, key]) => {
                  const cdePath = projectState?.carbon_pathway?.[key];
                  const eligible = cdePath?.eligible ?? false;
                  return (
                    <div key={key} className="py-1.5 border-b border-slate-50 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-600">{name}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                          eligible
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}>
                          {eligible ? "Structurally Aligned" : "Conditions Required"}
                        </span>
                      </div>
                      {!eligible && cdePath?.issues?.[0] && (
                        <p className="text-xs text-slate-400 italic mt-0.5">{cdePath.issues[0]}</p>
                      )}
                    </div>
                  );
                })}
                <button onClick={() => navigate(`/project/${id}/carbon-strategy`)}
                  className="mt-3 w-full text-xs text-teal-600 font-semibold hover:underline text-center">
                  Full Strategy →
                </button>
              </div>

              {/* Deal Room (Share + Conversation + Timeline + Doc Requests) — FI only */}
              {isFiUser && dealDetail && !dealDetail.is_terminal && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="deal-room">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Deal Room</p>
                  </div>

                  {/* Share Deal button */}
                  <div className="px-5 py-4 border-b border-slate-100">
                    {!showShare ? (
                      <button onClick={() => setShowShare(true)}
                        className="w-full flex items-center gap-2 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700"
                        data-testid="share-deal-btn">
                        <Share2 size={12} /> Share Deal with Investor
                      </button>
                    ) : shareResult ? (
                      <div data-testid="share-result">
                        <p className="text-xs font-bold text-emerald-700 mb-1">Shared! Copy investor link:</p>
                        <input readOnly value={shareResult.share_url} className="w-full text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" onClick={e => { e.target.select(); document.execCommand("copy"); toast.success("Link copied!"); }} />
                        <p className="text-xs text-slate-400 mt-1">Email sent: {shareResult.email_sent ? "Yes" : "No (copy link)"}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <input type="email" value={shareEmail} onChange={e => setShareEmail(e.target.value)}
                          placeholder="Investor email..." className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg" />
                        <input type="text" value={shareNote} onChange={e => setShareNote(e.target.value)}
                          placeholder="Optional note..." className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg" />
                        <div className="flex gap-2">
                          <button onClick={shareDeal} disabled={!shareEmail || sharing}
                            className="flex-1 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg disabled:opacity-50">
                            {sharing ? "Sending..." : "Send Invite"}
                          </button>
                          <button onClick={() => setShowShare(false)} className="px-3 py-1.5 border border-slate-200 text-xs rounded-lg">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Conversation */}
                  <div className="px-5 py-2.5 border-b border-slate-100">
                    <button onClick={() => setShowConvo(v => !v)}
                      className="w-full flex items-center justify-between text-xs text-slate-600 font-semibold"
                      data-testid="toggle-conversation">
                      <span className="flex items-center gap-1.5"><MessageCircle size={11} /> Conversation ({messages.length})</span>
                      {showConvo ? <ChevronRight size={11} className="rotate-90" /> : <ChevronRight size={11} />}
                    </button>
                    {showConvo && (
                      <div className="mt-2">
                        <div className="max-h-40 overflow-y-auto space-y-1.5 mb-2">
                          {messages.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">No messages yet. Start the conversation.</p>
                          ) : messages.map(m => (
                            <div key={m.id} className={`text-xs px-2 py-1.5 rounded-lg ${m.sender_role === "fi" ? "bg-blue-50 text-blue-700" : m.sender_role === "system" ? "bg-slate-50 text-slate-500 italic" : "bg-slate-100 text-slate-700"}`}>
                              <span className="font-semibold">{m.sender_name?.split(" ")[0] || m.sender_email?.split("@")[0]}:</span> {m.message?.slice(0,80)}
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-1.5">
                          <input value={newMsg} onChange={e => setNewMsg(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && sendMessage()}
                            placeholder="Type message..." className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
                          <button onClick={sendMessage} disabled={!newMsg || sendingMsg}
                            className="px-2 py-1.5 bg-[#0F172A] text-white rounded-lg disabled:opacity-50">
                            <Send size={10} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timeline */}
                  <div className="px-5 py-2.5">
                    <button onClick={() => { setShowTimeline(v => !v); if (!showTimeline) loadTimeline(); }}
                      className="w-full flex items-center justify-between text-xs text-slate-600 font-semibold"
                      data-testid="toggle-timeline">
                      <span className="flex items-center gap-1.5"><Clock size={11} /> Deal Timeline ({timeline.length})</span>
                      {showTimeline ? <ChevronRight size={11} className="rotate-90" /> : <ChevronRight size={11} />}
                    </button>
                    {showTimeline && (
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5">
                        {timeline.length === 0 ? <p className="text-xs text-slate-400 italic">No events yet.</p> :
                          timeline.map((e, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0 mt-1" />
                              <div>
                                <p className="font-semibold text-slate-700">{e.label}</p>
                                <p className="text-slate-400">{e.timestamp?.slice(0,10)} · {e.actor}</p>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}
              {sector && ["biogas","waste_recovery"].includes(sector) && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="finance-brief-card">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5">Finance Brief</p>
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Institutional-grade. 13 sections: feedstock economics, energy revenue, methane profile, sensitivity, carbon pathways, DFI evidence requirements.
                  </p>
                  <button onClick={downloadFinanceBrief} disabled={dlBrief}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
                    data-testid="download-finance-brief">
                    {dlBrief ? <><RefreshCw size={12} className="animate-spin" /> Generating...</> : <><Download size={12} /> Download Finance Brief</>}
                  </button>
                </div>
              )}

              {/* Project Owner: No financing process message */}
              {!isFiUser && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="no-fi-process">
                  <div className="flex items-start gap-3">
                    <Info size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">Financing Process</p>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Independent Verification Required. No financing process has been initiated for this project by a financing institution.
                      </p>
                      <p className="text-xs text-slate-400 italic mt-1.5">
                        Contact a DFI or financing institution to initiate a deal review process against this project's validation-ready outputs.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Credit Committee Memo — FI/Admin context only */}
              {isFiUser && !isEmpty && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="credit-memo-card">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5">Credit Committee Memo</p>
                  <p className="text-xs text-slate-500 mb-3">1–2 page decision summary with recommendation. Internal use only.</p>
                  <button onClick={async () => {
                    const token = localStorage.getItem("decarboniq_token");
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${id}/credit-memo`, { headers: { Authorization: `Bearer ${token}` } });
                      if (!res.ok) throw new Error();
                      const blob = await res.blob();
                      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "CreditMemo.pdf"; a.click(); URL.revokeObjectURL(a.href);
                    } catch { toast.error("Failed to generate Credit Memo."); }
                  }}
                    className="w-full flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-xs font-bold rounded-xl hover:bg-slate-800"
                    data-testid="download-credit-memo">
                    <Download size={12} /> Download Credit Memo
                  </button>
                </div>
              )}

              {/* Deal Lifecycle Workspace — FI/Admin only */}
              {isFiUser && investorStatus !== "do_not_present" && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="deal-workspace">
                  <div className="px-5 py-3 bg-[#0F172A] flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-white">
                        {dealDetail ? `Deal: ${dealDetail.deal_id}` : "Capital Activation"}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {dealDetail ? `${dealDetail.status?.replace(/_/g," ").toUpperCase()} — ${Math.round(dealDetail.pct_complete || 0)}% to close` : "Initiate to activate deal workflow"}
                      </p>
                    </div>
                    {dealDetail && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                        dealDetail.status === "financial_close" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                        dealDetail.status === "rejected"        ? "bg-red-50 text-red-600 border-red-200" :
                        "bg-teal-100 text-teal-800 border-teal-200"
                      }`}>{dealDetail.status?.replace(/_/g," ")}</span>
                    )}
                  </div>

                  {/* Stage tracker */}
                  {dealDetail && (
                    <div className="px-5 py-4 border-b border-slate-100" data-testid="stage-tracker">
                      <div className="flex items-center gap-0">
                        {dealDetail.all_stages?.map((s, i) => {
                          const isActive  = s.is_current && dealDetail.status !== "rejected";
                          const isDone    = s.is_complete;
                          const isLast    = i === dealDetail.all_stages.length - 1;
                          return (
                            <React.Fragment key={s.id}>
                              <div className="flex flex-col items-center">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                                  isDone  ? "bg-emerald-500 border-emerald-500 text-white" :
                                  isActive? "bg-teal-600 border-teal-600 text-white ring-2 ring-teal-200" :
                                            "bg-white border-slate-300 text-slate-400"
                                }`}>
                                  {isDone ? "✓" : i + 1}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5 whitespace-nowrap">{s.label}</p>
                              </div>
                              {!isLast && (
                                <div className={`h-0.5 flex-1 mx-0.5 mt-[-10px] ${isDone ? "bg-emerald-400" : "bg-slate-200"}`} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {dealDetail.status === "rejected" && (
                        <p className="text-xs text-red-600 mt-2 font-bold">REJECTED — Deal closed.</p>
                      )}
                    </div>
                  )}

                  {/* Next steps */}
                  {dealDetail?.next_steps?.length > 0 && (
                    <div className="px-5 py-4 border-b border-slate-100">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wide mb-1.5">Next Steps</p>
                      {dealDetail.next_steps.slice(0, 3).map((s, i) => (
                        <p key={i} className="text-xs text-slate-600 py-0.5">
                          <span className="text-teal-600 font-bold">{i+1}.</span> {s}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="px-5 py-3">
                    {!dealDetail ? (
                      // Not yet initiated
                      <button onClick={handleInitiateDeal} disabled={initiating}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50"
                        data-testid="initiate-deal-btn">
                        {initiating ? <><RefreshCw size={12} className="animate-spin" /> Initiating...</> : <><Zap size={12} /> Initiate Financing Process</>}
                      </button>
                    ) : dealDetail && !dealDetail.is_terminal ? (
                      // Active deal — contextual action buttons
                      <div className="flex flex-col gap-2">
                        {dealDetail.next_actions?.map(a => (
                          <button key={a.action}
                            onClick={() => handleAdvanceDeal(a.action)}
                            disabled={advancing}
                            className={`w-full flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl disabled:opacity-50 transition-colors ${
                              a.variant === "success" ? "bg-emerald-600 text-white hover:bg-emerald-700" :
                              a.variant === "danger"  ? "border border-red-200 text-red-600 hover:bg-red-50" :
                              "bg-teal-600 text-white hover:bg-teal-700"
                            }`}
                            data-testid={`deal-action-${a.action}`}>
                            {advancing ? <RefreshCw size={11} className="animate-spin" /> : null}
                            {a.label}
                            {a.gate && <span className="text-xs opacity-60 ml-1">({a.gate})</span>}
                          </button>
                        ))}
                      </div>
                    ) : dealDetail?.status === "financial_close" ? (
                      <div className="text-center py-2">
                        <CheckCircle2 size={20} className="text-emerald-500 mx-auto mb-1" />
                        <p className="text-xs font-bold text-emerald-700">Deal Closed</p>
                        <p className="text-xs text-slate-400">Capital deployed. Monitor MRV and repayment.</p>
                      </div>
                    ) : null}
                  </div>

                  {/* Stage history accordion */}
                  {dealDetail?.stage_history?.length > 0 && (
                    <div className="px-5 pb-3 border-t border-slate-100">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wide mt-2 mb-1.5">Audit Trail</p>
                      {dealDetail.stage_history.map((h, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                          <span className="text-teal-500 font-bold flex-shrink-0">→</span>
                          <span className="text-slate-600">{h.from} → {h.to}</span>
                          <span className="text-slate-400 ml-auto flex-shrink-0">{h.user} · {h.timestamp?.slice(0,10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
